// frontend/src/config/studioSiteConfig.js

export const DEFAULT_STUDIO_SITE_CONFIG = {
  meta: {
    allowedKinds: ["digital"],
  },
  content: {
    hero: {
      headline: "Too many AI tools. Not enough clarity.",
      tagline: "A growing catalog for clearer work",
      subline:
        "Start with what is slowing you down. Choose a practical tool, adapt it to your work, and shape a system that is entirely your own.",
    },
    intro: {
      text: "What this is: a growing catalog of practical tools. Who it's for: teams shipping products with AI. How it reads: browse the catalog, or start from your constraint.",
      productsSectionIntro: "Every tool, open on the table. Inspect before you commit.",
      shopCtaLabel: "Find the right tool",
      shopCtaUrl: "#find",
    },
    footer: {
      closingTagline: "Clarity, systems, and practical tools for working with AI.",
      companyName: "578 Studio",
      copyrightText: "© 2026 578 Productions LLC",
      signupButtonLabel: "",
      signupButtonUrl: "",
      shopButtonLabel: "Browse everything",
      shopButtonUrl: "#catalog",
    },
  },
  colors: {
    primary: "#E8A33A",
    secondary: "#2B7D6E",
    accentOrange: "#D66B28",
    accentTerracotta: "#E05A2D",
    accentSky: "#4DA8C4",
    leadline: "#1E1E1E",
    backgroundClear: "#F5EDE0",
    backgroundFrosted: "#F9F5EE",
    textDark: "#3A3530",
  },
  typography: {
    headingFont: "Federo",
    bodyFont: "Outfit",
  },
  sections: {
    hero: {
      backgroundColor: "backgroundClear",
      showBorder: true,
      showCornerAccents: true,
    },
    intro: {
      visible: true,
      backgroundColor: "backgroundClear",
    },
    products: {
      showDividers: true,
      showMosaicBar: false,
    },
    footer: {
      backgroundColor: "leadline",
      textColor: "backgroundClear",
      showMosaicBar: true,
    },
  },
  partners: [],
  productGroups: [
    { id: "catalog", title: "Catalog", intro: "", colorKey: "primary", textOnDark: false, order: 0 },
  ],
  products: [
    {
      id: "ai-governance-policy-pack",
      group: "catalog",
      order: 1,
      name: "AI Governance Policy Pack",
      description:
        "The governance framework we actually use — policies, procedures, and the operational scaffolding to make them real, not a document that sits in a drive.",
      accent: "terracotta",
      buttonLabel: "",
      buttonUrl: "",
      kind: "digital",
      format: "guide",
      storeId: "578-studio-ls",
      checkoutUrl: "",
    },
    {
      id: "library-card",
      group: "catalog",
      order: 2,
      name: "Library Card",
      description:
        "Your starting point. A free index of every tool, template, and framework in the 578 collection — see what's inside before you commit to anything.",
      accent: "teal",
      buttonLabel: "",
      buttonUrl: "",
      kind: "digital",
      format: "resource",
      storeId: "",
      checkoutUrl: "",
    },
    {
      id: "ai-builder-mission-control",
      group: "catalog",
      order: 3,
      name: "AI Builder Mission Control",
      description:
        "A system for tracking every moving piece when you're building with AI — from first idea to shipped product, without losing the thread.",
      accent: "sky",
      buttonLabel: "",
      buttonUrl: "",
      kind: "digital",
      format: "framework",
      storeId: "578-studio-ls",
      checkoutUrl: "",
    },
    {
      id: "ai-system-builder-toolkit",
      group: "catalog",
      order: 4,
      name: "AI System Builder Toolkit",
      description:
        "Research, models, and templates to help you architect AI systems with confidence instead of guesswork.",
      accent: "amber",
      buttonLabel: "",
      buttonUrl: "",
      kind: "digital",
      format: "toolkit",
      storeId: "578-studio-ls",
      checkoutUrl: "",
    },
    {
      id: "sessions-kits",
      group: "catalog",
      order: 5,
      name: "Sessions Kits",
      description:
        "Structured session plans for the conversations that matter — strategy, retros, planning, and reviews you can run without prep dread.",
      accent: "terracotta",
      buttonLabel: "",
      buttonUrl: "",
      kind: "digital",
      format: "template",
      storeId: "578-studio-ls",
      checkoutUrl: "",
    },
    {
      id: "product-hub",
      group: "catalog",
      order: 6,
      name: "Product Hub",
      description:
        "The operational center for running a product. One hub, every artifact, no scavenger hunts across a dozen tools.",
      accent: "teal",
      buttonLabel: "",
      buttonUrl: "",
      kind: "digital",
      format: "template",
      storeId: "578-studio-ls",
      checkoutUrl: "",
    },
  ],
  startHere: {
    enabled: true,
    title: "Start here",
    description: "Pick the situation that sounds most like your team. We'll point you to a practical first move.",
    questions: [
      {
        id: "priority",
        prompt: "What do you need first?",
        options: [
          {
            id: "ship-this-week",
            label: "Ship this week",
            weights: [
              { productId: "ai-builder-mission-control", score: 3 },
              { productId: "product-hub", score: 1 },
            ],
          },
          {
            id: "team-align",
            label: "Get the team aligned",
            weights: [
              { productId: "product-hub", score: 3 },
              { productId: "sessions-kits", score: 2 },
            ],
          },
          {
            id: "governance-first",
            label: "Reduce AI governance risk",
            weights: [
              { productId: "ai-governance-policy-pack", score: 3 },
              { productId: "library-card", score: 1 },
            ],
          },
        ],
      },
      {
        id: "maturity",
        prompt: "Where are you in your AI operating model?",
        options: [
          {
            id: "starting-out",
            label: "Starting out",
            weights: [
              { productId: "library-card", score: 3 },
              { productId: "sessions-kits", score: 1 },
            ],
          },
          {
            id: "already-building",
            label: "Already building, need better systems",
            weights: [
              { productId: "ai-builder-mission-control", score: 2 },
              { productId: "ai-system-builder-toolkit", score: 2 },
            ],
          },
        ],
      },
      {
        id: "work-style",
        prompt: "What kind of support do you want right now?",
        options: [
          {
            id: "quick-win",
            label: "A quick win I can apply today",
            weights: [
              { productId: "sessions-kits", score: 2 },
              { productId: "library-card", score: 1 },
            ],
          },
          {
            id: "deep-system",
            label: "A deeper system we can run long-term",
            weights: [
              { productId: "ai-system-builder-toolkit", score: 3 },
              { productId: "product-hub", score: 1 },
            ],
          },
        ],
      },
    ],
  },
  constraints: [
    { id: "missing-direction", label: "Missing direction", productId: "library-card" },
    { id: "team-alignment", label: "Team alignment", productId: "ai-builder-mission-control" },
    { id: "ship-faster", label: "Ship faster", productId: "ai-builder-mission-control" },
    { id: "evaluating-ai", label: "Evaluating AI", productId: "ai-system-builder-toolkit" },
  ],
  // NOTE: no `recommender` block — the ratified content contract (C2) records
  // the startHere wizard as superseding the old recommender; nothing reads it,
  // and seeding it would write a contract-unknown field into published config.
  // If a live row already carries one, SNDR-165's seed verification cleans it.
};
